import { useMutation } from '@apollo/client';
import { CURRENT_ACCOUNT } from '@application/api';
import { graphql } from '@gql/gql';
import type {
  AccountDogBreederImgUploadInput,
  MtBreederOnboardingGetProfileQuery,
  MtProfileBreederUpdateProfileMutation,
  MtProfileBreederUpdateProfileMutationVariables,
} from '@gql/graphql';
import type { MtProfileBreederValues } from '@presentation/pages';
import cleanDeep from 'clean-deep';
import { useCallback } from 'react';
import { MT_BREEDER_ONBOARDING_GET_PROFILE } from '../../MtBreederOnboardingPageController/api/useGetProfile';
import { useProfileUpdateBuilder } from './useProfileUpdateBuilder';

export const MT_PROFILE_BREEDER_UPDATE_PROFILE = graphql(`
  mutation MtProfileBreederUpdateProfile(
    $data: AccountUpdateInput!
    $profileImage: Upload
    $dogBreederHkFileUpload: Upload
    $dogBreederPermissionFilesUpload: [Upload]
    $dogBreederIdentityFilesUpload: [Upload]
    $dogBreederBreedClubFilesUpload: [Upload]
    $dogBreederCertificationFileUpload: Upload
    $dogBreederImagesUpload: [AccountDogBreederImgUploadInput!]
    $dogBreederImageIdsRemoved: [Int!]
    $femaleBreedersCount: Int
  ) {
    accountMeUpdate(
      data: $data
      profileImage: $profileImage
      dogBreederImagesUpload: $dogBreederImagesUpload
      dogBreederHkFileUpload: $dogBreederHkFileUpload
      dogBreederPermissionFilesUpload: $dogBreederPermissionFilesUpload
      dogBreederIdentityFilesUpload: $dogBreederIdentityFilesUpload
      dogBreederBreedClubFilesUpload: $dogBreederBreedClubFilesUpload
      dogBreederCertificationFileUpload: $dogBreederCertificationFileUpload
      dogBreederImageIdsRemoved: $dogBreederImageIdsRemoved
      femaleBreedersCount: $femaleBreedersCount
    ) {
      id
    }
  }
`);

export const useUpdateProfile = (
  profile?: MtBreederOnboardingGetProfileQuery['accountMe'] | null,
) => {
  const [updateProfile, { error, loading }] = useMutation<
    MtProfileBreederUpdateProfileMutation,
    MtProfileBreederUpdateProfileMutationVariables
  >(MT_PROFILE_BREEDER_UPDATE_PROFILE, {
    awaitRefetchQueries: true,
    refetchQueries: [MT_BREEDER_ONBOARDING_GET_PROFILE, CURRENT_ACCOUNT],
  });

  const { profileUpdateBuilder } = useProfileUpdateBuilder();

  const updateProfileFromForm = useCallback(
    (form: MtProfileBreederValues) => {
      const { data } = profileUpdateBuilder(form, profile);
      const formImageUrls: string[] = cleanDeep(form.images ?? [])
        ?.map((file) => (typeof file === 'string' ? file : undefined))
        .filter((file) => file != null) as string[];

      const dogBreederImageIdsRemoved = (profile?.dogBreeder?.imgs ?? [])
        .filter((img) => !formImageUrls.includes(img.small ?? ''))
        .map((img) => img.id);

      const dogBreederImagesUpload: AccountDogBreederImgUploadInput[] = cleanDeep(form.images ?? [])
        ?.map((file) =>
          typeof file === 'string'
            ? undefined
            : ({ file, type: 'picture' } as AccountDogBreederImgUploadInput),
        )
        .filter((upload) => upload?.file != null) as AccountDogBreederImgUploadInput[];

      const params = {
        variables: {
          ...(dogBreederImagesUpload?.length ? { dogBreederImagesUpload } : {}),
          ...(dogBreederImageIdsRemoved?.length ? { dogBreederImageIdsRemoved } : {}),
          ...(form.profileImage instanceof File ? { profileImage: form.profileImage } : {}),
          ...(form.hkFile && typeof form.hkFile !== 'string'
            ? { dogBreederHkFileUpload: form.hkFile }
            : {}),
          ...(form.certificationFile && typeof form.certificationFile !== 'string'
            ? { dogBreederCertificationFileUpload: form.certificationFile }
            : {}),
          ...(form.permissionFiles && typeof form.permissionFiles[0] !== 'string'
            ? { dogBreederPermissionFilesUpload: form.permissionFiles }
            : {}),
          ...(form.identityFiles && typeof form.identityFiles[0] !== 'string'
            ? { dogBreederIdentityFilesUpload: form.identityFiles }
            : {}),
          ...(form.breedClubFiles && typeof form.breedClubFiles[0] !== 'string'
            ? { dogBreederBreedClubFilesUpload: form.breedClubFiles }
            : {}),
          data,
          femaleBreedersCount: form.femaleBreedersCount,
        } as MtProfileBreederUpdateProfileMutationVariables,
      };

      return updateProfile(params);
    },
    [profileUpdateBuilder, profile, updateProfile],
  );

  const finishOnboarding = useCallback(async () => {
    return updateProfile({
      variables: {
        data: { dogBreeder: { update: { data: { hasFinishedOnboarding: { set: true } } } } },
      },
    });
  }, [updateProfile]);

  return {
    updateProfileLoading: loading,
    updateProfileError: error,
    updateProfileFromForm,
    updateProfile,
    finishOnboarding,
  };
};
